/**
 * Builds the space-separated `tags` string carried by a registry item's representation.
 *
 * Tags arrive from stock providers as a loose keyword dump: the same word repeats in different
 * cases, and much of it just restates the title or description. Every tag costs prompt tokens and
 * gives the agent another near-duplicate string to weigh, so we keep only the ones that actually
 * add information — a case-insensitive de-dupe, dropping any tag already present in the title or
 * description, capped at `maxTags` of whatever survives.
 */
export function formatTags(
    tags: string[],
    { title, description }: { title: string; description: string },
    maxTags = 10,
): string {
    const covered = new Set<string>();
    for (const word of `${title} ${description}`.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []) {
        covered.add(word);
    }

    const kept: string[] = [];
    const seen = new Set<string>();

    for (const tag of tags) {
        const trimmed = tag.trim();
        if (!trimmed) continue;

        const normalized = trimmed.toLowerCase();
        if (seen.has(normalized)) continue;
        seen.add(normalized);

        // A multi-word tag is only redundant when every one of its words is already covered.
        const words = normalized.match(/[\p{L}\p{N}]+/gu);
        if (words?.length && words.every((word) => covered.has(word))) continue;

        kept.push(trimmed);
        if (kept.length >= maxTags) break;
    }

    return kept.join(' ');
}
